/**
 * Dark-themed display map anchored on a single point.
 *
 * Uses Leaflet + CartoDB's free "Dark Matter" raster tiles — no API
 * key, gorgeous dark styling that matches the Suron palette, fast
 * CDN cache. Deliberately NOT interactive: dragging / zooming / tap
 * are all disabled because this is a "where did it happen" display,
 * not a navigation surface. The parent view surfaces an "Open in
 * Maps" link separately for users who want the native Apple Maps
 * experience.
 *
 * Custom pin replaces Leaflet's default blue shadow marker with a
 * warm pulsing dot styled to match the rest of the app. Attribution
 * is kept visible (required by the tile license) but small enough
 * not to compete with the pin.
 *
 * Only mounts once per lat/lng pair; re-mounts on coordinate change
 * so a fresh interaction doesn't inherit the prior map's state.
 */
import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import "./LocationMap.css";

interface LocationMapProps {
  latitude: number;
  longitude: number;
  zoom?: number;
}

export function LocationMap({
  latitude,
  longitude,
  zoom = 16,
}: LocationMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const map = L.map(container, {
      center: [latitude, longitude],
      zoom,
      zoomControl: false,
      attributionControl: true,
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      touchZoom: false,
      keyboard: false,
      boxZoom: false,
    });

    L.tileLayer(
      "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
      {
        subdomains: "abcd",
        maxZoom: 20,
        // Custom, terse attribution. Leaflet lets us replace the
        // default entirely — no "Improve this map" link, no bulky
        // full-license text. Still honors CartoDB + OSM attribution
        // requirements.
        attribution: "© OpenStreetMap · CARTO",
      }
    ).addTo(map);

    const icon = L.divIcon({
      className: "suron-pin",
      html: '<div class="pin-pulse"></div><div class="pin-dot"></div>',
      iconSize: [18, 18],
      iconAnchor: [9, 9],
    });

    L.marker([latitude, longitude], { icon, interactive: false }).addTo(map);

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [latitude, longitude, zoom]);

  return <div ref={containerRef} className="suron-map h-64 w-full" />;
}
